import React from 'react';
import { Link } from 'react-router-dom';
import { BlogArticleLayout } from '../../components/blog/BlogArticleLayout';

const QUICK_ANSWER = "Return on equity (ROE) measures how much profit a company generates from each dollar of shareholder equity. ROE = Net Income ÷ Shareholder Equity. An ROE of 15% means the company turned $15 of profit from every $100 of shareholder money. It is one of Warren Buffett's favourite single-number measures of business quality.";

const FAQS = [
  { q: 'What is a good ROE?', a: 'A long-term ROE above 15% is generally considered strong. Above 20% is excellent. The S&P 500 averages around 14%. Compare ROE to the company\'s own history and to direct competitors.' },
  { q: 'Why can leverage inflate ROE?', a: 'Equity is the denominator. If a company takes on debt to buy back shares, equity shrinks and ROE rises — even if profits are flat. Always cross-check ROE with debt levels.' },
  { q: 'Is ROE the same as ROA?', a: 'No. Return on assets (ROA) divides profit by total assets, including those funded by debt. ROA is harder to game with leverage and gives a fairer cross-industry comparison.' },
  { q: 'Can ROE be negative?', a: 'Yes. If a company posts a net loss, ROE is negative. If equity itself goes negative (more liabilities than assets), the ratio becomes meaningless and is usually shown as N/A.' },
  { q: 'Where do I find ROE for a stock?', a: 'Most stock data sites (Yahoo Finance, Stock Analysis, your broker) list it under key statistics. You can also calculate it yourself from the income statement and balance sheet.' },
];

export default function WhatIsRoePage() {
  return (
    <BlogArticleLayout
      slug="what-is-roe"
      title="What Is Return on Equity (ROE)?"
      description="ROE measures how efficiently a company turns shareholder money into profit. The formula, sector benchmarks, and the trap to avoid."
      keywords="ROE, return on equity, what is ROE, ROE formula, good ROE, how to calculate ROE, ROE vs ROA, ROE explained"
      quickAnswer={QUICK_ANSWER}
      quickAnswerText={QUICK_ANSWER}
      faqs={FAQS}
      related={['debt-to-equity-ratio', 'what-is-pe-ratio', 'how-to-read-a-balance-sheet']}
    >
      <section>
        <h2>What is return on equity?</h2>
        <p>
          ROE answers a single question: how much profit does this company create per dollar of shareholder money?
          The formula is: <strong>ROE = Net Income ÷ Shareholder Equity</strong>.
        </p>
        <p>
          Shareholder equity is the part of the balance sheet that belongs to investors after all liabilities are subtracted from assets.
          ROE tells you how productively management is putting that capital to work.
        </p>
      </section>

      <section>
        <h2>How do you calculate ROE?</h2>
        <p>
          Two numbers, both on the financial statements:
        </p>
        <ul>
          <li><strong>Net income</strong> — annual bottom-line profit (income statement).</li>
          <li><strong>Shareholder equity</strong> — total equity (balance sheet).</li>
        </ul>
        <p>
          Worked example: Apple's net income last year was ~$94B with average shareholder equity of ~$70B. ROE ≈ 94 ÷ 70 = <strong>~134%</strong>.
          That is exceptionally high — driven partly by huge buybacks shrinking the equity base.
        </p>
      </section>

      <section>
        <h2>What's a "good" ROE?</h2>
        <p>
          By sector, sustained over many years:
        </p>
        <ul>
          <li><strong>Below 10%</strong> — weak. Often capital-intensive industries (utilities, autos).</li>
          <li><strong>10–15%</strong> — average. Most healthy mature companies sit here.</li>
          <li><strong>15–25%</strong> — strong. Common for branded consumer companies and software.</li>
          <li><strong>Above 25%</strong> — excellent, but check for leverage or buybacks distorting the number.</li>
        </ul>
        <p>
          A consistently high ROE over 5–10 years is one of the strongest signals of a high-quality business. One-year spikes are easier to fake.
        </p>
      </section>

      <section>
        <h2>Why can ROE be misleading?</h2>
        <p>
          ROE has one big weakness: it is sensitive to leverage. Two ways management can artificially boost ROE without improving the business:
        </p>
        <ul>
          <li><strong>Debt-funded buybacks</strong> shrink the equity denominator while leaving income roughly the same.</li>
          <li><strong>Loading up on debt</strong> increases assets without adding equity, which can amplify returns when things go well — and amplify losses when they don't.</li>
        </ul>
        <p>
          Always look at ROE alongside <Link to="/blog/debt-to-equity-ratio">debt-to-equity</Link>. A 25% ROE on minimal debt is far more impressive than a 25% ROE built on leverage.
        </p>
      </section>

      <section>
        <h2>How do investors use ROE in practice?</h2>
        <p>
          Long-term investors like Buffett use ROE as a quality filter: find businesses that earn high returns on shareholder capital year after year, regardless of the macro environment.
          Compounding works in your favour when a company can repeatedly reinvest profits at attractive rates.
        </p>
        <p>
          Pair ROE with <Link to="/blog/what-is-free-cash-flow">free cash flow</Link> to see whether reported profits actually translate to cash, then check the <Link to="/blog/what-is-pe-ratio">P/E ratio</Link> for valuation.
        </p>
      </section>
    </BlogArticleLayout>
  );
}
